import { AlarmClock, Archive, ChevronDown, Inbox, Settings } from 'lucide-react';
import { useMemo } from 'react';
import type { QueueResponse } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// Virtual "Incoming" queue for display
const INCOMING_QUEUE: QueueResponse = {
	id: 0,
	slug: 'incoming',
	name: 'Incoming',
	color: '#6366f1',
	icon: null,
	position: -1,
};

// Special ID for archive view
export const ARCHIVE_QUEUE_ID = -1;

interface QueueSwitcherProps {
	queues: QueueResponse[];
	currentQueueId: number;
	onQueueChange: (queueId: number) => void;
	onManageQueues?: () => void;
	onQueueSettings?: (queue: QueueResponse) => void;
	linkCounts?: Map<number, number>;
	archivedCount?: number;
	showSnoozed?: boolean;
	onShowSnoozedChange?: (show: boolean) => void;
}

export function QueueSwitcher({
	queues,
	currentQueueId,
	onQueueChange,
	onManageQueues,
	onQueueSettings,
	linkCounts,
	archivedCount = 0,
	showSnoozed = false,
	onShowSnoozedChange,
}: QueueSwitcherProps) {
	// Combine incoming with user queues (filter out id=0 if API includes it)
	const allQueues = useMemo(() => {
		const userQueues = queues.filter((q) => q.id !== 0);
		return [INCOMING_QUEUE, ...userQueues];
	}, [queues]);

	// Find current queue
	const currentQueue = useMemo(() => {
		if (currentQueueId === ARCHIVE_QUEUE_ID) {
			return {
				id: ARCHIVE_QUEUE_ID,
				slug: 'archive',
				name: 'Archive',
				color: '#71717a',
				icon: null,
				position: 999,
			};
		}
		return allQueues.find((q) => q.id === currentQueueId) ?? INCOMING_QUEUE;
	}, [allQueues, currentQueueId]);

	const currentCount =
		currentQueueId === ARCHIVE_QUEUE_ID ? archivedCount : (linkCounts?.get(currentQueueId) ?? 0);

	// Get snoozed count for current queue
	const currentSnoozedCount = useMemo(() => {
		if (currentQueueId === ARCHIVE_QUEUE_ID) return 0;
		const queue = allQueues.find((q) => q.id === currentQueueId);
		return queue?.snoozedCount ?? 0;
	}, [allQueues, currentQueueId]);

	return (
		<>
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="outline" className="gap-2 min-w-[180px] justify-between font-mono">
					<div className="flex items-center gap-2">
						{currentQueue.id === ARCHIVE_QUEUE_ID ? (
							<Archive className="size-4 text-muted-foreground" />
						) : currentQueue.id === 0 ? (
							<Inbox className="size-4 text-muted-foreground" />
						) : (
							<span
								className="size-2 shrink-0"
								style={{ backgroundColor: currentQueue.color }}
							/>
						)}
						{currentQueue.icon && <span>{currentQueue.icon}</span>}
						<span>[{currentQueue.name.toUpperCase().slice(0, 8)}]</span>
						<span className="text-muted-foreground text-xs">{currentCount}</span>
					</div>
					<ChevronDown className="size-4 text-muted-foreground" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-[220px]">
				{/* Incoming queue */}
				<DropdownMenuItem
					onClick={() => onQueueChange(0)}
					className={cn('font-mono', currentQueueId === 0 && 'bg-primary/10 text-primary')}
				>
					<Inbox className="size-4 mr-2 text-muted-foreground" />
					<span className="flex-1">[INB] Incoming</span>
					{linkCounts?.has(0) && (
						<span className="text-xs text-muted-foreground">{linkCounts.get(0)}</span>
					)}
				</DropdownMenuItem>

				{queues.length > 0 && <DropdownMenuSeparator />}

				{/* User queues */}
				{queues.map((queue) => (
					<DropdownMenuItem
						key={queue.id}
						onClick={() => onQueueChange(queue.id)}
						className={cn('font-mono', currentQueueId === queue.id && 'bg-primary/10 text-primary')}
					>
						<span
							className="size-2 shrink-0 mr-2"
							style={{ backgroundColor: queue.color }}
						/>
						{queue.icon && <span className="mr-1">{queue.icon}</span>}
						<span className="flex-1">{queue.name}</span>
						{linkCounts?.has(queue.id) && (
							<span className="text-xs text-muted-foreground">{linkCounts.get(queue.id)}</span>
						)}
					</DropdownMenuItem>
				))}

				{/* Archive */}
				<DropdownMenuSeparator />
				<DropdownMenuItem
					onClick={() => onQueueChange(ARCHIVE_QUEUE_ID)}
					className={cn('font-mono', currentQueueId === ARCHIVE_QUEUE_ID && 'bg-primary/10 text-primary')}
				>
					<Archive className="size-4 mr-2 text-muted-foreground" />
					<span className="flex-1">[ARC] Archive</span>
					{archivedCount > 0 && (
						<span className="text-xs text-muted-foreground">{archivedCount}</span>
					)}
				</DropdownMenuItem>

				{/* Current queue settings */}
				{onQueueSettings && currentQueueId > 0 && (
					<DropdownMenuItem onClick={() => onQueueSettings(currentQueue as QueueResponse)}>
						<Settings className="size-4 mr-2 text-muted-foreground" />
						<span>Queue Settings</span>
					</DropdownMenuItem>
				)}
				{/* Manage queues */}
				{onManageQueues && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={onManageQueues}>
							<Settings className="size-4 mr-2 text-muted-foreground" />
							<span>Manage Queues</span>
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>

		{/* Snoozed badge - only show when there are snoozed links in current queue */}
		{currentSnoozedCount > 0 && currentQueueId !== ARCHIVE_QUEUE_ID && (
			<Badge
				variant={showSnoozed ? 'default' : 'secondary'}
				className={cn(
					'cursor-pointer transition-colors gap-1',
					showSnoozed && 'bg-amber-500 hover:bg-amber-600 text-white'
				)}
				onClick={() => onShowSnoozedChange?.(!showSnoozed)}
				title={showSnoozed ? 'Hide snoozed links' : 'Show snoozed links'}
			>
				<AlarmClock className="size-3" />
				<span>Snoozed ({currentSnoozedCount})</span>
			</Badge>
		)}
		</>
	);
}
